import Cookies from 'js-cookie';
import { create } from 'zustand';

const CONSENT_KEY = 'tmg_cookie_consent';
const LEGACY_CONSENT_KEY = 'cookiesAccepted';

const isHttps =
  typeof globalThis !== 'undefined' && globalThis.location?.protocol === 'https:';

const getCookieOptions = () => {
  const options: Cookies.CookieAttributes = {
    expires: 365,
    path: '/',
    sameSite: 'Lax',
  };
  if (isHttps) {
    options.secure = true;
  }
  if (
    typeof globalThis !== 'undefined' &&
    import.meta.env.VITE_COOKIE_DOMAIN_VALUE &&
    globalThis.location?.hostname !== 'localhost'
  ) {
    options.domain = import.meta.env.VITE_COOKIE_DOMAIN_VALUE;
  }
  return options;
};

export type CookiePreferences = {
  hasConsented: boolean;
  technical: boolean;
  analytics: boolean;
};

const DEFAULT_PREFERENCES: CookiePreferences = {
  hasConsented: false,
  technical: true,
  analytics: false,
};

function readCookiePreferences(): CookiePreferences {
  try {
    const raw = Cookies.get(CONSENT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        hasConsented: Boolean(parsed.hasConsented),
        technical: true,
        analytics: Boolean(parsed.analytics),
      };
    }
    const legacy = Cookies.get(LEGACY_CONSENT_KEY);
    if (legacy === 'true') {
      return {
        hasConsented: true,
        technical: true,
        analytics: true,
      };
    }
  } catch {
    // ignore
  }
  return DEFAULT_PREFERENCES;
}

interface CookieConsentStore {
  preferences: CookiePreferences;
  savePreferences: (analytics: boolean) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  resetConsent: () => void;
}

const useCookieConsentStore = create<CookieConsentStore>((set) => ({
  preferences: readCookiePreferences(),
  savePreferences: (analytics: boolean) => {
    const newPrefs: CookiePreferences = {
      hasConsented: true,
      technical: true,
      analytics,
    };
    const options = getCookieOptions();
    Cookies.set(CONSENT_KEY, JSON.stringify(newPrefs), options);
    Cookies.set(LEGACY_CONSENT_KEY, analytics ? 'true' : 'false', options);
    set({ preferences: newPrefs });
  },
  acceptAll: () => {
    const newPrefs: CookiePreferences = {
      hasConsented: true,
      technical: true,
      analytics: true,
    };
    const options = getCookieOptions();
    Cookies.set(CONSENT_KEY, JSON.stringify(newPrefs), options);
    Cookies.set(LEGACY_CONSENT_KEY, 'true', options);
    set({ preferences: newPrefs });
  },
  rejectAll: () => {
    const newPrefs: CookiePreferences = {
      hasConsented: true,
      technical: true,
      analytics: false,
    };
    const options = getCookieOptions();
    Cookies.set(CONSENT_KEY, JSON.stringify(newPrefs), options);
    Cookies.set(LEGACY_CONSENT_KEY, 'false', options);
    set({ preferences: newPrefs });
  },
  resetConsent: () => {
    const options = getCookieOptions();
    Cookies.remove(CONSENT_KEY, options);
    Cookies.remove(LEGACY_CONSENT_KEY, options);
    set({ preferences: DEFAULT_PREFERENCES });
  },
}));

export const useCookieConsent = () => {
  const preferences = useCookieConsentStore((state) => state.preferences);
  const savePreferences = useCookieConsentStore((state) => state.savePreferences);
  const acceptAll = useCookieConsentStore((state) => state.acceptAll);
  const rejectAll = useCookieConsentStore((state) => state.rejectAll);
  const resetConsent = useCookieConsentStore((state) => state.resetConsent);

  return {
    hasConsent: preferences.hasConsented,
    analyticsAccepted: preferences.analytics,
    preferences,
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
    setConsent: acceptAll,
  };
};
